import React from 'react';
import PropTypes from 'prop-types';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import { Bert } from 'meteor/themeteorchef:bert';

import { Box, IconButton } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';

import { isEmpty, cloneDeep, filter } from 'lodash';
import Loading from '../../Loading';

import TemplateImapsItemEdit from './TemplateImapsItemEdit';
import useTimeStore from '../../ZustandProvider';

import Maps from '../../../../api/Maps/Maps';
import Layers from '../../../../api/Layers/Layers';
import MapContainerViewMap from '../../../containers/MapContainerViewMap';
import * as mapOps from '../../../../api/Maps/map-operations';
import * as templateOps from '../../../../api/AnnotationTemplates/annotation-template-operations';

import { URL_REGEX, YOUTUBE_URL_REGEX } from '../../../../api/Videos/Videos';
import OpenModalFromUrl from '../../OpenModalFromUrl';
import ButtonOnMap from '../../../containers/ButtonOnMap';

const EDITION_MODE = 'edition';
const PREVIEW_MODE = 'preview';

const TemplateItemImaps = ({
  elementItem,
  previewMode,
  updateAnnotationFields,
  maps,
  layers,
  loading,
  deployEnv,
  updatedAnnotation,
  isNotesComponent,
  ...rest
}) => {
  if (loading) return <Loading />;

  const setCoordsAnnotationTemplate = useTimeStore(state => state.setCoordsAnnotationTemplate);
  const coordOnMap = useTimeStore(state => state.coordOnMap);

  const [baseMap, setBaseMap] = React.useState([]);
  const [layersMaps, setLayersMaps] = React.useState([]);
  const [map, setMap] = React.useState({});
  const [mapIdSelected, setMapIdSelected] = React.useState(
    templateOps.getElementMapId(elementItem)
  );
  const [baseMapsZoomState, setBaseMapsZoomState] = React.useState(
    templateOps.getElementZoom(elementItem)
  );
  const [annotationCoordinatesState, setAnnotationCoordinatesState] = React.useState(
    templateOps.getElementCoordinates(elementItem)
  );
  const [linkShared, setLinkShared] = React.useState(templateOps.getElementLinkMap(elementItem));
  const [showErrorLink, setShowErrorLink] = React.useState(false);

  const [isMapLoaded, setIsMapLoaded] = React.useState(false);

  const [leafletMap, setLeafletMap] = React.useState({});
  const [coordsToCenterMap, setCoordsToCenterMap] = React.useState(
    templateOps.getElementCoordsCenter(elementItem)
  );
  const [modeCenter, setModeCenter] = React.useState(templateOps.getElementCenterMode(elementItem));
  const [openFullScreen, setOpenFullScreen] = React.useState(false);
  const [heightMapState, setHeightMap] = React.useState(
    templateOps.getElementHeightMap(elementItem)
  );

  const handleMapSelected = idMap => {
    setMapIdSelected(idMap);
    if (idMap !== 'none') {
      const mapSelect = filter(maps, element => mapOps.getId(element) === idMap);
      if (!isEmpty(mapSelect)) {
        const baseMaps = mapOps.getLayerObjectsArray(mapSelect[0], layers, false);
        const layersMapsArray = mapOps.getLayerObjectsArray(mapSelect[0], layers);

        const clonedMapSelect = cloneDeep(mapSelect[0]);
        clonedMapSelect.mapLocation.properties.zoom = baseMapsZoomState;
        setBaseMap(baseMaps);
        setLayersMaps(layersMapsArray);
        setMap(clonedMapSelect);
        setLinkShared('');
        if (previewMode === EDITION_MODE)
          updateAnnotationFields(elementItem, 'map', {
            mapId: idMap,
            zoom: baseMapsZoomState,
            coordinates: annotationCoordinatesState,
            linkMap: '',
            centerMode: modeCenter,
            coordsCenter: coordsToCenterMap
          });
      }
    } else {
      setBaseMap([]);
      setLayersMaps([]);
      setMap({});
    }
  };

  const handleZoomMap = zoom => {
    if (!isEmpty(map)) {
      const clonedMapSelect = cloneDeep(map);
      clonedMapSelect.mapLocation.properties.zoom = Number(zoom);
      setMap(clonedMapSelect);
    }
    setBaseMapsZoomState(Number(zoom));
    if (previewMode === EDITION_MODE)
      updateAnnotationFields(elementItem, 'map', {
        mapId: mapIdSelected,
        zoom: Number(zoom),
        coordinates: annotationCoordinatesState,
        linkMap: '',
        centerMode: modeCenter,
        coordsCenter: coordsToCenterMap
      });
  };

  const handleHeightMap = height => {
    if (Number.isNaN(Number(height))) {
      Bert.alert('Height not valid', 'danger');
    } else {
      updateAnnotationFields(elementItem, 'map', {
        mapId: mapIdSelected,
        zoom: Number(baseMapsZoomState),
        coordinates: annotationCoordinatesState,
        linkMap: '',
        centerMode: modeCenter,
        coordsCenter: coordsToCenterMap,
        heightMap: height
      });
      setHeightMap(height);
    }
  };

  const handleUpdateLocation = location => {
    if (!isEmpty(location) && previewMode === EDITION_MODE) {
      const { zoom } = location;
      setBaseMapsZoomState(Number(zoom));
      if (previewMode === EDITION_MODE)
        updateAnnotationFields(elementItem, 'map', {
          mapId: mapIdSelected,
          zoom: Number(zoom),
          coordinates: annotationCoordinatesState,
          linkMap: '',
          centerMode: modeCenter,
          coordsCenter: coordsToCenterMap
        });
    }
  };

  const handleLinkMap = urlMap => {
    const isValid = YOUTUBE_URL_REGEX.test(urlMap) || URL_REGEX.test(urlMap);
    if (isValid) {
      setLinkShared(urlMap);
      setMapIdSelected('none');
      setShowErrorLink(false);
      if (previewMode === EDITION_MODE)
        updateAnnotationFields(elementItem, 'map', {
          mapId: 'none',
          zoom: baseMapsZoomState,
          coordinates: annotationCoordinatesState,
          linkMap: urlMap
        });
    } else {
      setShowErrorLink(true);
    }
  };

  const handleMapLoaded = (isLoaded, mapLeaflet) => {
    setLeafletMap(mapLeaflet);
    setIsMapLoaded(isLoaded);
  };

  const handleCenterMap = (coordsCenter, mode) => {
    const floatLat = parseFloat(coordsCenter[0]);
    const floatLong = parseFloat(coordsCenter[1]);
    const coords = [floatLat, floatLong];
    setCoordsToCenterMap(coords);
    setModeCenter(mode);
    if (previewMode === EDITION_MODE)
      updateAnnotationFields(elementItem, 'map', {
        mapId: mapIdSelected,
        zoom: baseMapsZoomState,
        coordinates: annotationCoordinatesState,
        linkMap: '',
        centerMode: mode,
        coordsCenter: coords
      });

    if (!isEmpty(leafletMap)) leafletMap.setView(coords);
  };

  React.useEffect(() => {
    handleMapSelected(templateOps.getElementMapId(elementItem));
    const coordsTemplate = templateOps.getElementCoordinates(elementItem);
    if (!isEmpty(coordsTemplate)) {
      setCoordsAnnotationTemplate({
        geoJSON: { geometry: { coordinates: coordsTemplate } }
      });
    } else if (!isEmpty(updatedAnnotation) && deployEnv === 'gmv') {
      const coordsAnnotionUpdated = updatedAnnotation.geoJSON.geometry.coordinates;
      if (!isEmpty(coordsAnnotionUpdated)) {
        setAnnotationCoordinatesState(coordsAnnotionUpdated);
        setCoordsAnnotationTemplate({
          geoJSON: { geometry: { coordinates: coordsAnnotionUpdated } }
        });
      }
    }
    if (!isEmpty(leafletMap)) {
      const coordsCenterMapTemplate = templateOps.getElementCoordsCenter(elementItem);
      if (!isEmpty(coordsCenterMapTemplate)) leafletMap.setView(coordsCenterMapTemplate);
      else leafletMap.setView([coordsTemplate[1], coordsTemplate[0]]);
    }
  }, [elementItem]);

  React.useEffect(() => {
    if (isMapLoaded) {
      if (!isEmpty(annotationCoordinatesState))
        setCoordsAnnotationTemplate({
          geoJSON: { geometry: { coordinates: annotationCoordinatesState } }
        });
      if (!isEmpty(coordsToCenterMap)) leafletMap.setView(coordsToCenterMap);
      else if (!isEmpty(annotationCoordinatesState))
        leafletMap.setView([annotationCoordinatesState[1], annotationCoordinatesState[0]]);
    }
  }, [isMapLoaded]);

  if (previewMode === EDITION_MODE && isNotesComponent) {
    React.useEffect(() => {
      if (!isEmpty(coordOnMap)) {
        const { lat, lng } = coordOnMap;
        const coords = [lng, lat];
        setAnnotationCoordinatesState(coords);
        setCoordsAnnotationTemplate({
          geoJSON: { geometry: { coordinates: coords } }
        });
      }
    }, [coordOnMap]);
  }

  const mapComponent = () => (
    <>
      <MapContainerViewMap
        baseMaps={baseMap}
        deployEnv={deployEnv}
        layers={layersMaps}
        map={map}
        externalBoton
        handleMapLoaded={handleMapLoaded}
        leafletMapId={`imaps-${mapIdSelected}`}
        height={`${heightMapState}vh`}
      />
      <ButtonOnMap numberOfButtonsRight={2}>
        <IconButton
          style={{
            color: '#2A3F90',
            backgroundColor: 'white',
            height: '34px',
            width: '34px',
            borderColor: '#2A3F90'
          }}
          onClick={() => setOpenFullScreen(true)}
        >
          <FullscreenIcon />
        </IconButton>
      </ButtonOnMap>
    </>
  );

  const mapComponentFullScreen = () => (
    <MapContainerViewMap
      baseMaps={baseMap}
      deployEnv={deployEnv}
      layers={layersMaps}
      map={map}
      externalBoton
      handleMapLoaded={handleMapLoaded}
      leafletMapId="imaps-fullScreen"
      height="100vh"
    />
  );

  return (
    <>
      {previewMode === PREVIEW_MODE && (
        <>
          {!isEmpty(map) && !isEmpty(baseMap) && isEmpty(linkShared) && (
            <>
              <Box
                id="mapBox"
                style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  marginBottom: '4%'
                }}
              >
                {mapComponent()}
              </Box>

              <OpenModalFromUrl
                isFullScreen
                isImage={false}
                isLoading
                openModal={openFullScreen}
                setOpenModal={setOpenFullScreen}
                url=""
                title="map full screen"
                isComponent
                component={mapComponentFullScreen}
              />
            </>
          )}
          {isEmpty(map) && isEmpty(baseMap) && !isEmpty(linkShared) && (
            <iframe src={linkShared} width="100%" height="400px" align="center" title="mapShared" />
          )}
        </>
      )}
      {previewMode === EDITION_MODE && (
        <>
          <TemplateImapsItemEdit
            deployEnv={deployEnv}
            elementItem={elementItem}
            updateAnnotationFields={updateAnnotationFields}
            updatedAnnotation={updatedAnnotation}
            baseMap={baseMap}
            layersMaps={layersMaps}
            map={map}
            maps={maps}
            mapIdSelected={mapIdSelected}
            baseMapsZoomState={baseMapsZoomState}
            linkShared={linkShared}
            handleMapSelected={handleMapSelected}
            handleZoomMap={handleZoomMap}
            handleUpdateLocation={handleUpdateLocation}
            handleLinkMap={handleLinkMap}
            externalButton
            handleMapLoaded={handleMapLoaded}
            isNotesComponent={isNotesComponent}
            showErrorLink={showErrorLink}
            layers={layers}
            leafletMap={leafletMap}
            handleCenterMap={handleCenterMap}
            handleHeightMap={handleHeightMap}
            heightMap={heightMapState}
            {...rest}
          />
        </>
      )}
    </>
  );
};
TemplateItemImaps.defaultProps = {
  deployEnv: 'gmv',
  maps: [],
  layers: [],
  updateAnnotationFields: () => console.error('no action to update'),
  updatedAnnotation: {},
  isNotesComponent: false
};

TemplateItemImaps.propTypes = {
  deployEnv: PropTypes.string,
  previewMode: PropTypes.string.isRequired,
  elementItem: PropTypes.object.isRequired,
  updateAnnotationFields: PropTypes.func,
  maps: PropTypes.array,
  layers: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  updatedAnnotation: PropTypes.object,
  isNotesComponent: PropTypes.bool
};

export default withTracker(() => {
  const mapsSub = Meteor.isClient && Meteor.subscribe('maps');
  const layersSub = Meteor.subscribe('layers');
  return {
    loading: !mapsSub.ready() || !layersSub.ready(),
    maps: Maps.find({}, { sort: { name: 1 } }).fetch(),
    layers: Layers.find({ deleted: { $eq: 'no' } }).fetch()
  };
})(TemplateItemImaps);
